// Missed-instalment chase + 7-day split (tv-qta, Feature B). On a MISSED top-up
// the Agent runs a full 3-sequence chase to retain the booking (vault Diagram 02,
// settled 18 Aug): prefer chasing an existing client to refilling the spot.
//
//   Reached -> pays          the instalment clears; the plan continues.
//   Reached -> wants to move  outside 7d the agent moves it directly + takes a
//                             partial payment; inside 7d it needs Ops approval.
//   No pickup after 3 goes -> CANCELLED, then the 7-day split on the cancel:
//                             inside 7d  -> FORFEIT (a new deposit is needed);
//                             outside 7d -> KEEP as a deposit-no-date (12 months)
//                                           -> the Sweeper pool.
//
// Domain logic only: this is NOT surfaced into the agent queue or any screen -
// Wave 2 (tv-9vr) does the queue surfacing (which touches the queue files Wave 0
// owns). Reuses tv-bli's forfeitDeposit + onFileExpiryIso. "Now" is the sim clock,
// never Date.now(); money is whole AUD dollars.

import { differenceInCalendarDays } from 'date-fns';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import { forfeitDeposit } from '@/domain/depositFlow';
import { onFileExpiryIso } from '@/domain/confirmationFlow';
import { isTopupPlanComplete } from '@/domain/topupPlan';
import { depositPaidAud } from '@/utils/bookingSummary';
import type { DepositLedgerEntry, TopupInstalment, TopupPlan } from '@/domain/types';

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

interface FlowOpts {
  by?: string;
  at?: string;
}

/** How many unanswered chase sequences end the retention effort and cancel. */
const MAX_CHASE_SEQUENCES = 3;

/**
 * Which side of the 7-day line the plan's booking sits on, measured from `at`
 * against the booking's appointment date (`booking.start`). A dateless plan (no
 * bookingId, or a booking that has gone missing) cannot be inside 7 days, so it
 * defaults to 'outside'. Whole calendar days, so it flips on the day boundary
 * exactly like the confirmation-cancel split (vault Diagram 06).
 */
export function topupSevenDaySplit(plan: TopupPlan, at: string): 'inside' | 'outside' {
  const booking = plan.bookingId ? useBrainStore.getState().bookings[plan.bookingId] : undefined;
  if (!booking) return 'outside';
  return differenceInCalendarDays(new Date(booking.start), new Date(at)) < 7 ? 'inside' : 'outside';
}

/** The index of the instalment the chase is about: the earliest `missed`, else
 *  the earliest `reminded` (the reconciler marks a miss from a reminded one). */
function currentChaseIndex(plan: TopupPlan): number {
  const missed = plan.instalments.findIndex((i) => i.status === 'missed');
  if (missed !== -1) return missed;
  return plan.instalments.findIndex((i) => i.status === 'reminded');
}

/**
 * Reached -> the client pays the currently-missed instalment. Marks the earliest
 * `missed` (fallback `reminded`) instalment `paid` (+ paidAt), appends a `topup`
 * ledger entry to the deposit so its paid balance grows by the instalment amount,
 * resets chaseAttempts to 0, and flips the plan to `completed` once every
 * instalment is settled. Idempotent no-op on an already-settled plan. Publishes
 * `topup:chase-paid`.
 */
export function topupChasePays(planId: string, opts?: FlowOpts): { ok: boolean } {
  const brain = useBrainStore.getState();
  const plan = brain.topupPlans[planId];
  if (!plan) return { ok: false };
  const at = opts?.at ?? nowIso();
  const by = opts?.by ?? 'system';
  if (plan.status !== 'active') return { ok: true }; // already completed / cancelled

  const idx = currentChaseIndex(plan);
  if (idx === -1) return { ok: false }; // nothing missed or reminded to pay

  const instalment = plan.instalments[idx]!;
  const instalments: TopupInstalment[] = plan.instalments.map((inst, i) =>
    i === idx ? { ...inst, status: 'paid', paidAt: at } : inst,
  );
  const nextPlan: TopupPlan = { ...plan, instalments, chaseAttempts: 0 };
  const status: TopupPlan['status'] = isTopupPlanComplete(nextPlan) ? 'completed' : plan.status;
  brain.upsertTopupPlan({ ...nextPlan, status });

  // Grow the deposit's paid balance by this instalment. Preserve the prior paid
  // history (backfill one `paid` entry when the deposit has no ledger yet) so the
  // ledger-derived balance rises rather than replacing what was already in.
  const deposit = brain.deposits[plan.depositId];
  if (deposit) {
    const priorPaid = depositPaidAud(deposit);
    const baseLedger: DepositLedgerEntry[] =
      deposit.ledger && deposit.ledger.length > 0
        ? deposit.ledger
        : priorPaid > 0
          ? [{ at: deposit.paidAt ?? at, amountAud: priorPaid, kind: 'paid', by: deposit.takenByAgentId ?? 'system' }]
          : [];
    brain.upsertDeposit({
      ...deposit,
      amountPaid: priorPaid + instalment.amountAud,
      ledger: [
        ...baseLedger,
        { at, amountAud: instalment.amountAud, kind: 'topup', by, note: 'Missed top-up chased and paid' },
      ],
    });
  }

  brain.appendActivity({
    id: `act-topup-chase-paid-${plan.id}-${at}`,
    type: 'deposit-paid',
    customerId: plan.customerId,
    studioId: plan.studioId,
    amount: instalment.amountAud,
    body: `Chased top-up paid: $${instalment.amountAud}. Plan back on track.`,
    timestamp: at,
  });
  eventBus.publish('topup:chase-paid', {
    planId: plan.id,
    depositId: plan.depositId,
    amountAud: instalment.amountAud,
    at,
  });
  return { ok: true };
}

/**
 * Reached -> wants to move the date. Returns the split decision only; the move
 * itself is out of scope for this bead (no full reschedule):
 *   outside 7d -> 'agent-moves-partial' (agent moves it + takes a partial payment)
 *   inside 7d  -> 'needs-ops-approval'  (a flagged decision, mechanics TBC)
 * Records the decision on the activity feed + publishes `topup:chase-move`.
 */
export function topupChaseMove(
  planId: string,
  opts?: FlowOpts,
): { ok: boolean; mode: 'agent-moves-partial' | 'needs-ops-approval' } {
  const brain = useBrainStore.getState();
  const plan = brain.topupPlans[planId];
  if (!plan) return { ok: false, mode: 'needs-ops-approval' };
  const at = opts?.at ?? nowIso();
  const split = topupSevenDaySplit(plan, at);
  const mode = split === 'outside' ? 'agent-moves-partial' : 'needs-ops-approval';

  brain.appendActivity({
    id: `act-topup-chase-move-${plan.id}-${at}`,
    type: 'outbound-call',
    customerId: plan.customerId,
    studioId: plan.studioId,
    body:
      mode === 'agent-moves-partial'
        ? 'Client wants to move the date (outside 7 days). Agent moves it and takes a partial payment now.'
        : 'Client wants to move the date (inside 7 days). Flagged for Ops approval.',
    timestamp: at,
  });
  eventBus.publish('topup:chase-move', { planId: plan.id, split, mode, at });
  return { ok: true, mode };
}

/**
 * A chase sequence went unanswered. Increments chaseAttempts and, on the 3rd
 * (>= MAX_CHASE_SEQUENCES) unanswered sequence, runs cancelMissedTopup (the
 * 7-day split) and reports cancelled. Publishes `topup:chase-no-answer`. A no-op
 * on a plan that is no longer active. Guard: unknown planId -> { ok: false }.
 */
export function topupChaseNoAnswer(
  planId: string,
  opts?: FlowOpts,
): { ok: boolean; attempts: number; cancelled: boolean } {
  const brain = useBrainStore.getState();
  const plan = brain.topupPlans[planId];
  if (!plan) return { ok: false, attempts: 0, cancelled: false };
  const at = opts?.at ?? nowIso();
  if (plan.status !== 'active') {
    return { ok: true, attempts: plan.chaseAttempts ?? 0, cancelled: plan.status === 'cancelled' };
  }

  const attempts = (plan.chaseAttempts ?? 0) + 1;
  brain.upsertTopupPlan({ ...plan, chaseAttempts: attempts });
  brain.appendActivity({
    id: `act-topup-chase-noanswer-${plan.id}-${attempts}`,
    type: 'voicemail',
    customerId: plan.customerId,
    studioId: plan.studioId,
    body: `Missed top-up chase, sequence ${attempts} of ${MAX_CHASE_SEQUENCES}: no answer.`,
    timestamp: at,
  });
  eventBus.publish('topup:chase-no-answer', { planId: plan.id, attempts, at });

  if (attempts >= MAX_CHASE_SEQUENCES) {
    cancelMissedTopup(plan.id, { by: opts?.by, at });
    return { ok: true, attempts, cancelled: true };
  }
  return { ok: true, attempts, cancelled: false };
}

/**
 * No pickup after the 3rd sequence (or an explicit cancel). Applies the 7-day
 * split against the booking's appointment date and cancels the plan (and its
 * booking, which is now dead):
 *   inside 7d  -> forfeitDeposit (deposit lost; a new deposit is needed to rebook)
 *   outside 7d -> keep the deposit as a deposit-no-date: unlink its booking and
 *                 stamp a 12-month on-file expiry, so the money-cohort / Sweeper
 *                 picks it up (paid deposits with no bookingId) with no queue wiring.
 * Idempotent no-op on an already-cancelled plan. Publishes `topup:cancelled`.
 * Guard: unknown planId -> { ok: false }.
 */
export function cancelMissedTopup(
  planId: string,
  opts?: FlowOpts,
): { ok: boolean; split: 'inside' | 'outside' } {
  const brain = useBrainStore.getState();
  const plan = brain.topupPlans[planId];
  if (!plan) return { ok: false, split: 'outside' };
  const at = opts?.at ?? nowIso();
  const by = opts?.by ?? 'system';
  const split = topupSevenDaySplit(plan, at);
  if (plan.status === 'cancelled') return { ok: true, split }; // already cancelled

  if (split === 'inside') {
    // INSIDE 7 days: the deposit is forfeit to the house. forfeitDeposit writes
    // the ledger movement and leaves the deposit linked; it never flips the
    // booking to no-show, so we cancel the (now dead) booking ourselves.
    if (plan.bookingId) {
      forfeitDeposit(plan.bookingId, { reason: 'Missed top-up, cancelled inside 7 days', by, at });
      const booking = brain.bookings[plan.bookingId];
      if (booking && booking.status !== 'cancelled') {
        brain.upsertBooking({ ...booking, status: 'cancelled' });
      }
    }
  } else {
    // OUTSIDE 7 days: keep the paid deposit on file as a dateless deposit (12-month
    // validity), so the Sweeper pool surfaces it. Unlink it from the dead booking
    // and cancel that booking.
    const deposit = brain.deposits[plan.depositId];
    if (deposit) {
      brain.upsertDeposit({ ...deposit, bookingId: undefined, expiresAt: onFileExpiryIso(at) });
    }
    if (plan.bookingId) {
      const booking = brain.bookings[plan.bookingId];
      if (booking && booking.status !== 'cancelled') {
        brain.upsertBooking({ ...booking, status: 'cancelled' });
      }
    }
  }

  brain.upsertTopupPlan({ ...brain.topupPlans[planId]!, status: 'cancelled' });
  brain.appendActivity({
    id: `act-topup-cancelled-${plan.id}-${at}`,
    type: 'booking-cancelled',
    customerId: plan.customerId,
    studioId: plan.studioId,
    body:
      split === 'inside'
        ? 'Top-up chase exhausted inside 7 days: booking cancelled, deposit forfeit.'
        : 'Top-up chase exhausted outside 7 days: booking cancelled, deposit kept on file (12 months).',
    timestamp: at,
  });
  eventBus.publish('topup:cancelled', { planId: plan.id, depositId: plan.depositId, split, at });
  return { ok: true, split };
}
